/**
 * Admin Scope Helper
 * 
 * Determines the scope of access for admin users and provides
 * utilities for fetching users within appropriate scope boundaries.
 * 
 * Scope Hierarchy:
 * - platform: Can see all users across all tenants/workspaces
 * - tenant: Can see users within their tenant
 * - workspace: Can see users within their workspace
 * - none: Regular user, no admin access
 */

import type { ActorRole } from '../types/prioritization/roles';
import { getSafeSupabaseClient } from './supabaseSync';

// ============================================================================
// Types
// ============================================================================

export type AdminScope = 'platform' | 'tenant' | 'workspace' | 'none';

export interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ScopeContext {
  scope: AdminScope;
  tenantId?: string;
  workspaceId?: string;
}

// ============================================================================
// Scope Determination
// ============================================================================

/**
 * Get the admin scope for a given role
 */
export function getAdminScope(role: ActorRole | null | undefined): AdminScope {
  if (!role) return 'none';
  if (role === 'platform-admin' || role === 'system-admin') {
    return 'platform';
  }
  if (role === 'tenant-admin') {
    return 'tenant';
  }
  if (role === 'workspace-admin') {
    return 'workspace';
  }
  return 'none';
}

/** Normalize DB/display role labels so assignment names match admin slugs */
function normalizedAdminSlug(name: string): string {
  const n = name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
  if (n === 'workspace-administrator') return 'workspace-admin';
  if (n === 'tenant-administrator') return 'tenant-admin';
  if (n === 'platform-administrator' || n === 'system-admin') return 'platform-admin';
  return n;
}

/**
 * Effective admin scope for settings/admin UIs.
 * Combines the legacy role picker (`currentRole`) with DB `role_assignments` names so
 * workspace admins who aren't selected as `workspace-admin` in the picker still get admin UX.
 */
export function resolveAdminScope(
  currentRole: ActorRole | null | undefined,
  assignedRoleNames: string[] | undefined | null,
): AdminScope {
  let best: AdminScope = getAdminScope(currentRole);
  const rank = (s: AdminScope) => (s === 'platform' ? 3 : s === 'tenant' ? 2 : s === 'workspace' ? 1 : 0);
  const bump = (s: AdminScope) => {
    if (rank(s) > rank(best)) best = s;
  };
  for (const raw of assignedRoleNames ?? []) {
    const n = normalizedAdminSlug(String(raw));
    if (n === 'platform-admin') bump('platform');
    else if (n === 'tenant-admin') bump('tenant');
    else if (n === 'workspace-admin') bump('workspace');
  }
  return best;
}

/**
 * Check if a role is any type of admin
 */
export function isAdminRole(role: ActorRole | null | undefined): boolean {
  return getAdminScope(role) !== 'none';
}

/**
 * Check if a role can see users across all tenants
 */
export function isPlatformAdmin(role: ActorRole | null | undefined): boolean {
  return getAdminScope(role) === 'platform';
}

/**
 * Check if a role can manage tenant settings
 */
export function canManageTenant(role: ActorRole): boolean {
  const scope = getAdminScope(role);
  return scope === 'platform' || scope === 'tenant';
}

/**
 * Check if a role can manage workspace settings
 */
export function canManageWorkspace(role: ActorRole): boolean {
  return getAdminScope(role) !== 'none';
}

// ============================================================================
// User Fetching
// ============================================================================

/**
 * Fetch users based on the admin scope
 * 
 * @param scope - The admin scope level
 * @param tenantId - The current tenant ID (required for tenant/workspace scope)
 * @param workspaceId - The current workspace ID (required for workspace scope)
 * @returns Array of users visible to the admin
 */
export async function fetchUsersForScope(
  scope: AdminScope,
  tenantId?: string,
  workspaceId?: string
): Promise<User[]> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    console.warn('[adminScope] Supabase not available');
    return [];
  }

  try {
    switch (scope) {
      case 'platform': {
        // Platform admin sees all users
        const { data, error } = await supabase
          .from('users')
          .select('id, name, email, created_at, updated_at')
          .order('name', { ascending: true });
        
        if (error) {
          console.error('[adminScope] Error fetching all users:', error);
          return [];
        }
        return data || [];
      }

      case 'tenant': {
        // Tenant admin sees users in their tenant
        if (!tenantId) {
          console.warn('[adminScope] No tenant ID provided for tenant scope');
          return [];
        }
        
        // Get user IDs from tenant memberships
        const { data: memberships, error: membershipError } = await supabase
          .from('tenant_memberships')
          .select('user_id')
          .eq('tenant_id', tenantId);
        
        if (membershipError) {
          console.error('[adminScope] Error fetching tenant memberships:', membershipError);
          return [];
        }
        
        if (!memberships || memberships.length === 0) {
          return [];
        }
        
        const userIds = memberships.map(m => m.user_id);
        
        const { data, error } = await supabase
          .from('users')
          .select('id, name, email, created_at, updated_at')
          .in('id', userIds)
          .order('name', { ascending: true });
        
        if (error) {
          console.error('[adminScope] Error fetching tenant users:', error);
          return [];
        }
        return data || [];
      }

      case 'workspace': {
        // Workspace admin sees users in their workspace
        if (!workspaceId) {
          console.warn('[adminScope] No workspace ID provided for workspace scope');
          return [];
        }
        
        // Get user IDs from workspace memberships
        const { data: memberships, error: membershipError } = await supabase
          .from('workspace_memberships')
          .select('user_id')
          .eq('workspace_id', workspaceId);
        
        if (membershipError) {
          console.error('[adminScope] Error fetching workspace memberships:', membershipError);
          return [];
        }
        
        if (!memberships || memberships.length === 0) {
          return [];
        }
        
        const userIds = memberships.map(m => m.user_id);
        
        const { data, error } = await supabase
          .from('users')
          .select('id, name, email, created_at, updated_at')
          .in('id', userIds)
          .order('name', { ascending: true });
        
        if (error) {
          console.error('[adminScope] Error fetching workspace users:', error);
          return [];
        }
        return data || [];
      }

      case 'none':
      default:
        // Non-admins don't see other users through admin queries
        return [];
    }
  } catch (err) {
    console.error('[adminScope] Exception fetching users:', err);
    return [];
  }
}

/**
 * Get the current scope context from stores
 * This is a convenience function that reads from tenantStore
 */
export function getScopeContext(
  role: ActorRole,
  currentTenantId?: string,
  currentWorkspaceId?: string
): ScopeContext {
  const scope = getAdminScope(role);
  
  return {
    scope,
    tenantId: scope === 'tenant' || scope === 'workspace' ? currentTenantId : undefined,
    workspaceId: scope === 'workspace' ? currentWorkspaceId : undefined,
  };
}
